import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  TextField,
  MenuItem,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MainLayout from '@/components/MainLayout';
import AppointmentDialog from '@/components/AppointmentDialog';
import TimeGrid from '@/components/calendar/TimeGrid';
import MonthGrid from '@/components/calendar/MonthGrid';
import {
  addDays,
  addMonths,
  buildWeekDays,
  formatRange,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from '@/components/calendar/dateUtils';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import useNewParam from '@/lib/useNewParam';

const ALL_EMPLOYEES = '__all__';

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('gymify_user') || 'null');
  } catch {
    return null;
  }
};

const CalendarPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [members, setMembers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phones default to the day view — a 7-column week grid is unreadable there.
  const [view, setView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
      ? 'day'
      : 'week',
  );
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [employeeFilter, setEmployeeFilter] = useState(ALL_EMPLOYEES);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'edit', appointment: null, initialStart: null });

  const currentUser = useMemo(() => readStoredUser(), []);

  const fetchData = async () => {
    try {
      const [apptRes, svcRes, memRes, usersRes, sitesRes] = await Promise.all([
        api.get('/appointments'),
        api.get('/services'),
        api.get('/members', { params: { status: 'active' } }),
        api.get('/users'),
        api.get('/sites'),
      ]);
      setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
      setServices(Array.isArray(svcRes.data) ? svcRes.data : []);
      setMembers(Array.isArray(memRes.data) ? memRes.data : []);
      const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
      setStaff(allUsers.filter((u) => u.role !== 'member'));
      setSites(Array.isArray(sitesRes.data) ? sitesRes.data : []);
    } catch {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const employees = useMemo(() => {
    return staff
      .map((u) => ({
        id: u.user_id,
        label: u.user_id === currentUser?.user_id ? `${u.name} (you)` : u.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [staff, currentUser]);

  const filteredAppointments = useMemo(() => {
    if (employeeFilter === ALL_EMPLOYEES) return appointments;
    return appointments.filter((a) => a.trainer_id === employeeFilter);
  }, [appointments, employeeFilter]);

  const primarySite = useMemo(() => sites.find((s) => s.is_primary) || sites[0] || null, [sites]);
  const businessHours = primarySite?.business_hours || null;

  const handleNav = (dir) => {
    if (view === 'day') setAnchor((d) => addDays(d, dir));
    else if (view === 'week') setAnchor((d) => addDays(d, 7 * dir));
    else setAnchor((d) => addMonths(d, dir));
  };

  const handleToday = () => setAnchor(startOfDay(new Date()));

  const handleViewChange = (_e, next) => {
    if (!next) return;
    setView(next);
    if (next === 'month') setAnchor((d) => startOfMonth(d));
    else if (next === 'week') setAnchor((d) => startOfWeek(d));
    else setAnchor((d) => startOfDay(d));
  };

  const openEdit = (appt) => {
    setDialogState({ open: true, mode: 'edit', appointment: appt, initialStart: null });
  };

  const openCreate = (start = null) => {
    setDialogState({ open: true, mode: 'create', appointment: null, initialStart: start });
  };

  const closeDialog = () => setDialogState((s) => ({ ...s, open: false }));

  useNewParam(() => openCreate());

  const days = useMemo(() => {
    if (view === 'day') return [startOfDay(anchor)];
    if (view === 'week') return buildWeekDays(anchor);
    return [];
  }, [view, anchor]);

  return (
    <MainLayout title="Calendar">
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {loading ? (
          <Skeleton variant="rounded" height={560} />
        ) : (
          // One frame: toolbar and grid share a single outlined card instead
          // of a title block + toolbar Paper + grid stacked separately.
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack
              direction="row"
              spacing={1.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ px: 2.25, py: 1.75, borderBottom: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" spacing={0.25} alignItems="center">
                <IconButton size="small" onClick={() => handleNav(-1)} data-testid="cal-prev">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={handleToday}
                  data-testid="cal-today"
                  sx={{ fontWeight: 600, borderColor: 'divider', px: 1.75 }}
                >
                  Today
                </Button>
                <IconButton size="small" onClick={() => handleNav(1)} data-testid="cal-next">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
                {formatRange(view, anchor)}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <TextField
                select
                size="small"
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                sx={{ minWidth: 170, '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
                data-testid="employee-filter"
              >
                <MenuItem value={ALL_EMPLOYEES}>All staff</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.label}
                  </MenuItem>
                ))}
              </TextField>

              <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={handleViewChange}
                data-testid="view-switcher"
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 999,
                  p: 0.375,
                  gap: 0.25,
                  '& .MuiToggleButton-root': {
                    border: 0,
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.5,
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'primary.main' },
                    },
                  },
                }}
              >
                <ToggleButton value="day">Day</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openCreate()}
                data-testid="add-appointment-button"
                sx={{ px: 2 }}
              >
                Book
              </Button>
            </Stack>

            {view === 'month' ? (
              <MonthGrid
                bare
                anchor={anchor}
                appts={filteredAppointments}
                onApptClick={openEdit}
                onSlotClick={openCreate}
              />
            ) : (
              <TimeGrid
                bare
                days={days}
                appts={filteredAppointments}
                businessHours={businessHours}
                onApptClick={openEdit}
                onSlotClick={openCreate}
              />
            )}
          </Paper>
        )}

        <AppointmentDialog
          open={dialogState.open}
          mode={dialogState.mode}
          appointment={dialogState.appointment}
          initialStart={dialogState.initialStart}
          services={services}
          members={members}
          employees={employees}
          businessHours={businessHours}
          currentUserId={currentUser?.user_id}
          onClose={closeDialog}
          onSaved={fetchData}
        />
      </Box>
    </MainLayout>
  );
};

export default CalendarPage;
